import React from 'react';
import { Header, HoverCard, Group, Burger, Drawer, Container } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Link, useLocation } from 'react-router-dom';
import { navbarStyles as useStyles } from './Styles/GlobalStyles';
import { navLinks } from '../../Data/data';
import { IconBrandInstagram, IconBrandFacebookFilled, IconBrandTiktokFilled } from '@tabler/icons-react';
import Logo from './Logo'
import { BASE_URL } from '../../Data/data';

const SOCIAL_LINKS = [
    { label: 'Facebook', href: 'https://www.facebook.com/NomadCollection', Icon: IconBrandFacebookFilled },
    { label: 'Instagram', href: 'https://www.instagram.com/julijuneau/', Icon: IconBrandInstagram },
    { label: 'TikTok', href: 'https://www.tiktok.com/@julijuneauglass', Icon: IconBrandTiktokFilled },
];

const SocialLinks = ({ className, iconClassName }) => (
    <div className={className}>
        {SOCIAL_LINKS.map(({ label, href, Icon }) => (
            <a key={label} href={href} target='_blank' rel='noreferrer' aria-label={label}>
                <Icon size={20} className={iconClassName} />
            </a>
        ))}
    </div>
);

// Text styles shared with TextTabs: uppercase, semibold, wide spacing; white when selected
const MENU_TEXT = 'uppercase text-sm font-semibold tracking-wider no-underline transition-colors duration-200';
const menuColor = (active) => (active ? 'text-white' : 'text-zinc-500 hover:text-zinc-200');
const drawerColor = (active) => (active ? 'text-white' : 'text-zinc-400');

const Navbar = () => {
    const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);
    const { classes, cx } = useStyles();

    // From the router, so the highlight updates on client-side navigation
    const { pathname } = useLocation();

    const smallPath = pathname.split('/')[1]

    return (
        <div className='sticky top-0 z-50 '>
            <Header height={40} px="md" className={[classes.header, classes.hiddenMobile]}>
                <Group position="apart" noWrap sx={{ height: '100%' }}>
                    <Link to='/'>
                        <div className='ml-8 md:ml-0 lg:ml-0 flex align-middle'>
                            <Logo />
                        </div>
                    </Link>

                    <Group noWrap sx={{ height: '100%' }} spacing={0}>
                        {navLinks.map((item) => {
                            const itemClass = cx(classes.link, (pathname === item.link || smallPath === item.itemTitle.toLowerCase()) && classes.linkActive);

                            return (
                            <HoverCard closeDelay={0} key={item.itemTitle}>
                                <HoverCard.Target>
                                    {/* Items without a page of their own (Glass) only open their dropdown */}
                                    {item.link
                                        ? <a href={`${BASE_URL}${item.link}`} className={itemClass}>
                                            {item.itemTitle}
                                        </a>
                                        : <span className={itemClass} style={{ cursor: 'default' }}>
                                            {item.itemTitle}
                                        </span>
                                    }
                                </HoverCard.Target>
                                {item.dropdownItems.length > 0 ?
                                    <HoverCard.Dropdown className='-mt-2 p-2 rounded-lg bg-[#1A1B1E] border border-solid border-zinc-800'>
                                        {item.dropdownItems.map((dropdownItems) => (
                                            <a
                                                key={dropdownItems.title}
                                                href={`${BASE_URL}${dropdownItems.link}`}
                                                className={`block px-3 py-2 ${MENU_TEXT} ${menuColor(pathname === dropdownItems.link)}`}
                                            >
                                                {dropdownItems.title}
                                            </a>
                                        ))}
                                    </HoverCard.Dropdown> : <></>}
                            </HoverCard>
                            );
                        })}
                    </Group>

                    <SocialLinks className='flex gap-3 mr-8 md:mr-0 lg:mr-0' iconClassName={classes.socialLinks} />
                </Group>
            </Header>


            {/************************************    MOBILE HEADER     *******************************/}
            <Header height={40} px="md" className={[classes.mobileHeader, classes.hiddenDesktop]}>
                <Container className={classes.inner}>
                    <Group position="left" sx={{ height: '100%' }} spacing={0} className='p-0 sm:-ml-6'>
                        <Link to='/'>
                            <div className=' flex align-middle'>
                                <Logo />
                            </div>
                        </Link>
                    </Group>

                    <Burger opened={drawerOpened} onClick={toggleDrawer} color='#a1a1aa' size='sm' aria-label='Menu' className='sm:-mr-6' />

                    <Drawer.Root
                        opened={drawerOpened}
                        onClose={closeDrawer}
                        position='right'
                        size='xs'
                        zIndex={1000000}
                    >
                        <Drawer.Overlay />

                        {/* Starts below the header, so it's that much shorter than the screen */}
                        <Drawer.Content className='bg-black mt-10 h-[calc(100%-40px)]'>
                            <Drawer.Body className='p-0'>
                                <nav aria-label='Menu'>
                                    {navLinks.map((item) => {
                                        const active = pathname === item.link || smallPath === item.itemTitle.toLowerCase();
                                        const rowClass = 'block px-5 py-3 border-solid border-0 border-b border-zinc-800';

                                        return (
                                        <div key={item.itemTitle}>
                                            {item.link
                                                ? <a href={item.link} className={`${rowClass} ${MENU_TEXT} ${drawerColor(active)}`}>
                                                    {item.itemTitle}
                                                </a>
                                                // Items without a page of their own (Glass) only label their links
                                                : <span className={`${rowClass} ${MENU_TEXT} text-zinc-500`}>
                                                    {item.itemTitle}
                                                </span>
                                            }

                                            {item.dropdownItems.map((dropdownItems) => (
                                                <a
                                                    key={dropdownItems.title}
                                                    href={dropdownItems.link}
                                                    className={`${rowClass} pl-9 ${MENU_TEXT} ${drawerColor(pathname === dropdownItems.link)}`}
                                                >
                                                    {dropdownItems.title}
                                                </a>
                                            ))}
                                        </div>
                                        );
                                    })}

                                    <SocialLinks className='flex justify-center gap-8 py-5' iconClassName={classes.socialLinks} />
                                </nav>
                            </Drawer.Body>
                        </Drawer.Content>
                    </Drawer.Root>
                </Container>
            </Header>
        </div>
    );
}

export default Navbar
